/**
 * Browser-based storage using IndexedDB for auto-save
 * and .leedoc (ZIP) files for import/export.
 */

import { StorageBackend } from './StorageBackend.js';
import { openDB, IDBPDatabase } from 'idb';
import JSZip from 'jszip';
import {
  computeHash,
  sanitizeFilename,
  applyRetentionPolicy,
  shouldCreateSnapshot,
  generateSnapshotId
} from './utils.js';
import {
  getImageDimensions,
  sanitizeMediaFilename,
  generateUniqueFilename,
  generateExportBlob,
  type ExportProjectData,
} from '../../shared/index.js';
import type {
  ListFilesResult,
  ReadFileResult,
  ReadComponentResult,
  SaveResult,
  CreateResult,
  DeleteResult,
  RenameResult,
  SnapshotMetadata,
  Snapshot,
  SnapshotReason,
  CreateSnapshotResult,
  RestoreSnapshotResult,
  StorageMetadata,
  ProjectInfo,
  ImportResult,
  ImportOptions,
  MediaItem,
  MediaUploadResult,
  MediaUsage,
  MediaListResult,
  MediaDeleteResult,
  ProjectFileContent,
} from './types.js';

interface FileRecord {
  projectId: string;
  path: string;
  content: string;
  lastModified: number;
}

interface ComponentRecord {
  projectId: string;
  name: string;
  content: string;
  lastModified: number;
}

interface ProjectRecord {
  id: string;
  name: string;
  created: number;
  lastModified: number;
}

interface HistoryRecord {
  projectId: string;
  filePath: string;
  id: string;
  timestamp: number;
  reason: SnapshotReason;
  content: string;
}

interface MediaRecord {
  projectId: string;
  filename: string;
  originalName: string;
  mimeType: string;
  size: number;
  width?: number;
  height?: number;
  data: ArrayBuffer;
  uploadedBy?: string;
  contentHash: string;
  createdAt: number;
}

interface ChecklistRecord {
  projectId: string;
  filePath: string;
  checkedSlugs: string[];
}

interface LeedocDB {
  projects: ProjectRecord;
  files: FileRecord;
  components: ComponentRecord;
  fileHistory: HistoryRecord;
  media: MediaRecord;
  checklist: ChecklistRecord;
}

export class BrowserStorageBackend extends StorageBackend {
  private db: IDBPDatabase<LeedocDB> | null = null;
  private currentProjectId: string | null = null;
  private initialized = false;

  constructor() {
    super('browser');
  }

  /**
   * Initialize IndexedDB connection
   */
  async init(): Promise<void> {
    if (this.initialized) return;

    this.db = await openDB<LeedocDB>('LeedocDB', 5, {
      upgrade(db, oldVersion) {
        // Store for projects (metadata)
        if (!db.objectStoreNames.contains('projects')) {
          const projects = db.createObjectStore('projects', { keyPath: 'id' });
          projects.createIndex('name', 'name');
          projects.createIndex('lastModified', 'lastModified');
        }
        // Store for files within projects
        if (!db.objectStoreNames.contains('files')) {
          const files = db.createObjectStore('files', { keyPath: ['projectId', 'path'] });
          files.createIndex('projectId', 'projectId');
        }
        // Store for components within projects
        if (!db.objectStoreNames.contains('components')) {
          const components = db.createObjectStore('components', { keyPath: ['projectId', 'name'] });
          components.createIndex('projectId', 'projectId');
        }
        // Store for file-level version history (added in v3)
        if (!db.objectStoreNames.contains('fileHistory')) {
          const fileHistory = db.createObjectStore('fileHistory', {
            keyPath: ['projectId', 'filePath', 'id']
          });
          fileHistory.createIndex('projectId', 'projectId');
          fileHistory.createIndex('projectFile', ['projectId', 'filePath']);
        }
        // Store for media files (added in v4)
        if (!db.objectStoreNames.contains('media')) {
          const media = db.createObjectStore('media', {
            keyPath: ['projectId', 'filename']
          });
          media.createIndex('projectId', 'projectId');
          media.createIndex('contentHash', ['projectId', 'contentHash']);
        }
        // Store for checklist progress (added in v5)
        if (!db.objectStoreNames.contains('checklist')) {
          const checklist = db.createObjectStore('checklist', {
            keyPath: ['projectId', 'filePath']
          });
          checklist.createIndex('projectId', 'projectId');
        }
      }
    });

    this.initialized = true;
  }

  async listFiles(): Promise<ListFilesResult> {
    await this.init();

    if (!this.currentProjectId || !this.db) {
      return { files: [], components: [] };
    }

    const files = await this.db.getAllFromIndex('files', 'projectId', this.currentProjectId);
    const components = await this.db.getAllFromIndex('components', 'projectId', this.currentProjectId);

    const formattedFiles = files.map(f => {
      const parts = f.path.split('/');
      return {
        name: parts[parts.length - 1],
        path: f.path,
        dir: parts.length >= 2 ? parts[parts.length - 2] : 'uncategorized'
      };
    });

    const formattedComponents = components.map(c => ({ name: c.name }));

    return { files: formattedFiles, components: formattedComponents };
  }

  async getAllContents(): Promise<ProjectFileContent[]> {
    await this.init();

    if (!this.currentProjectId || !this.db) return [];

    const files = await this.db.getAllFromIndex('files', 'projectId', this.currentProjectId);
    const components = await this.db.getAllFromIndex('components', 'projectId', this.currentProjectId);

    return [
      ...files.map(f => ({ path: f.path, content: f.content })),
      ...components.map(c => ({ path: `_components/${c.name}`, content: c.content })),
    ];
  }

  async readFile(path: string): Promise<ReadFileResult> {
    await this.init();

    if (!this.currentProjectId || !this.db) {
      throw new Error('No project selected');
    }

    const file = await this.db.get('files', [this.currentProjectId, path]);
    if (!file) {
      throw new Error('File not found');
    }

    return {
      content: file.content,
      variables: this.extractVariables(file.content)
    };
  }

  async readComponent(name: string): Promise<ReadComponentResult> {
    await this.init();

    if (!this.currentProjectId || !this.db) {
      throw new Error('No project selected');
    }

    const component = await this.db.get('components', [this.currentProjectId, name]);
    if (!component) {
      throw new Error('Component not found');
    }

    return {
      content: component.content,
      path: `_components/${name}`,
      name: name
    };
  }

  async saveFile(path: string, content: string): Promise<SaveResult> {
    await this.init();

    if (!this.currentProjectId || !this.db) {
      throw new Error('No project selected');
    }

    // Check if snapshot needed before saving
    await this.maybeCreateSnapshot(path);

    const now = Date.now();
    // Components live in their own object store (keyed by name, not path) —
    // every read path (readComponent, export, history) looks there, so saves
    // must route there too or component edits silently never take effect.
    if (path.startsWith('_components/')) {
      await this.db.put('components', {
        projectId: this.currentProjectId,
        name: path.replace('_components/', ''),
        content,
        lastModified: now
      });
    } else {
      await this.db.put('files', {
        projectId: this.currentProjectId,
        path,
        content,
        lastModified: now
      });
    }

    await this.updateProjectTimestamp();

    const hash = await computeHash(content);

    return {
      success: true,
      hash,
      updatedAt: new Date(now).toISOString()
    };
  }

  async createFile(path: string, content?: string): Promise<CreateResult> {
    await this.init();

    if (!this.currentProjectId || !this.db) {
      throw new Error('No project selected');
    }

    const defaultContent = content || `<!-- vars
@HOSTNAME=example.com
-->
# New Document

Write your documentation here.
`;

    await this.db.put('files', {
      projectId: this.currentProjectId,
      path,
      content: defaultContent,
      lastModified: Date.now()
    });

    await this.updateProjectTimestamp();
    return { success: true, path };
  }

  async createComponent(name: string, content?: string): Promise<CreateResult> {
    await this.init();

    if (!this.currentProjectId || !this.db) {
      throw new Error('No project selected');
    }

    const defaultContent = content || `## Component

<!-- tangle: output.sh -->
\`\`\`bash
# Your script here
\`\`\`
`;

    await this.db.put('components', {
      projectId: this.currentProjectId,
      name,
      content: defaultContent,
      lastModified: Date.now()
    });

    await this.updateProjectTimestamp();
    return { success: true, path: `_components/${name}`, name };
  }

  async deleteFile(path: string): Promise<DeleteResult> {
    await this.init();

    if (!this.currentProjectId || !this.db) {
      throw new Error('No project selected');
    }

    await this.createSnapshot(path, 'pre_delete');

    await this.db.delete('files', [this.currentProjectId, path]);
    // Clean up checklist data for deleted file
    try { await this.db.delete('checklist', [this.currentProjectId, path]); } catch (_) { /* ignore */ }
    await this.updateProjectTimestamp();
    return { success: true };
  }

  async deleteComponent(name: string): Promise<DeleteResult> {
    await this.init();

    if (!this.currentProjectId || !this.db) {
      throw new Error('No project selected');
    }

    const componentPath = `_components/${name}`;
    await this.createSnapshot(componentPath, 'pre_delete');

    await this.db.delete('components', [this.currentProjectId, name]);
    // Clean up checklist data for deleted component
    try { await this.db.delete('checklist', [this.currentProjectId, componentPath]); } catch (_) { /* ignore */ }
    await this.updateProjectTimestamp();
    return { success: true };
  }

  async renameFile(oldPath: string, newPath: string): Promise<RenameResult> {
    await this.init();

    if (!this.currentProjectId || !this.db) {
      throw new Error('No project selected');
    }

    const file = await this.db.get('files', [this.currentProjectId, oldPath]);
    if (!file) {
      throw new Error('File not found');
    }

    await this.db.put('files', {
      projectId: this.currentProjectId,
      path: newPath,
      content: file.content,
      lastModified: Date.now()
    });

    await this.db.delete('files', [this.currentProjectId, oldPath]);
    await this.updateProjectTimestamp();

    return { success: true };
  }

  async renameComponent(oldName: string, newName: string): Promise<RenameResult> {
    await this.init();

    if (!this.currentProjectId || !this.db) {
      throw new Error('No project selected');
    }

    const component = await this.db.get('components', [this.currentProjectId, oldName]);
    if (!component) {
      throw new Error('Component not found');
    }

    await this.db.put('components', {
      projectId: this.currentProjectId,
      name: newName,
      content: component.content,
      lastModified: Date.now()
    });

    await this.db.delete('components', [this.currentProjectId, oldName]);
    await this.updateProjectTimestamp();

    return { success: true };
  }

  // ==================== Project Management ====================

  async createProject(name: string): Promise<string> {
    await this.init();
    if (!this.db) throw new Error('Database not initialized');

    const id = `project-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    await this.db.put('projects', {
      id,
      name,
      created: Date.now(),
      lastModified: Date.now()
    });

    this.currentProjectId = id;
    return id;
  }

  async listProjects(): Promise<ProjectRecord[]> {
    await this.init();
    if (!this.db) return [];

    const projects = await this.db.getAll('projects');
    return projects.sort((a, b) => b.lastModified - a.lastModified);
  }

  async loadProject(projectId: string): Promise<ProjectRecord> {
    await this.init();
    if (!this.db) throw new Error('Database not initialized');

    const project = await this.db.get('projects', projectId);
    if (!project) {
      throw new Error('Project not found');
    }

    this.currentProjectId = projectId;
    return project;
  }

  async getCurrentProject(): Promise<ProjectRecord | null> {
    await this.init();
    if (!this.currentProjectId || !this.db) return null;
    const project = await this.db.get('projects', this.currentProjectId);
    return project ?? null;
  }

  async deleteProject(projectId: string): Promise<void> {
    await this.init();
    if (!this.db) return;

    const files = await this.db.getAllFromIndex('files', 'projectId', projectId);
    for (const f of files) {
      await this.db.delete('files', [projectId, f.path]);
    }

    const components = await this.db.getAllFromIndex('components', 'projectId', projectId);
    for (const c of components) {
      await this.db.delete('components', [projectId, c.name]);
    }

    const snapshots = await this.db.getAllFromIndex('fileHistory', 'projectId', projectId);
    for (const s of snapshots) {
      await this.db.delete('fileHistory', [projectId, s.filePath, s.id]);
    }

    // Delete media files
    const mediaFiles = await this.db.getAllFromIndex('media', 'projectId', projectId);
    for (const m of mediaFiles) {
      await this.db.delete('media', [projectId, m.filename]);
    }

    await this.db.delete('projects', projectId);

    if (this.currentProjectId === projectId) {
      this.currentProjectId = null;
    }
  }

  async renameProject(projectId: string, newName: string): Promise<void> {
    await this.init();
    if (!this.db) throw new Error('Database not initialized');

    const project = await this.db.get('projects', projectId);
    if (!project) {
      throw new Error('Project not found');
    }

    project.name = newName;
    project.lastModified = Date.now();
    await this.db.put('projects', project);
  }

  // ==================== Import/Export ====================

  async exportProject(): Promise<Blob> {
    await this.init();

    if (!this.currentProjectId || !this.db) {
      throw new Error('No project selected');
    }

    const project = await this.db.get('projects', this.currentProjectId);
    const files = await this.db.getAllFromIndex('files', 'projectId', this.currentProjectId);
    const components = await this.db.getAllFromIndex('components', 'projectId', this.currentProjectId);
    const mediaFiles = await this.db.getAllFromIndex('media', 'projectId', this.currentProjectId);
    const allSnapshots = await this.db.getAllFromIndex('fileHistory', 'projectId', this.currentProjectId);

    // Build export data structure
    const exportData: ExportProjectData = {
      name: project?.name ?? 'project',
      files: [
        ...files.map(f => ({ path: f.path, content: f.content })),
        ...components.map(c => ({ path: `_components/${c.name}`, content: c.content })),
      ],
      history: allSnapshots.map(s => ({
        filePath: s.filePath,
        id: s.id,
        timestamp: s.timestamp,
        reason: s.reason,
        content: s.content,
      })),
      media: mediaFiles.map(m => ({
        filename: m.filename,
        originalName: m.originalName,
        mimeType: m.mimeType,
        size: m.size,
        width: m.width ?? null,
        height: m.height ?? null,
        uploadedBy: m.uploadedBy ?? null,
        data: m.data,
      })),
    };

    return await generateExportBlob(exportData, {
      onWarning: (msg) => console.warn(msg),
    });
  }

  async downloadProject(): Promise<{ success: boolean }> {
    const project = await this.getCurrentProject();
    if (!project) {
      throw new Error('No project selected');
    }

    const blob = await this.exportProject();
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = `${sanitizeFilename(project.name)}.leedoc`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    URL.revokeObjectURL(url);
    return { success: true };
  }

  async importProject(file: File, options: ImportOptions = {}): Promise<ImportResult> {
    await this.init();
    if (!this.db) throw new Error('Database not initialized');

    const zip = await JSZip.loadAsync(file);

    let metadataFile = zip.file('project.json');
    let basePath = '';

    if (!metadataFile) {
      const files = Object.keys(zip.files);
      const projectJsonPath = files.find(f => f.endsWith('project.json') && !f.includes('_generated/'));
      if (projectJsonPath) {
        metadataFile = zip.file(projectJsonPath);
        basePath = projectJsonPath.replace('project.json', '');
      }
    }

    let projectName: string;
    if (metadataFile) {
      const metadata = JSON.parse(await metadataFile.async('string'));
      projectName = metadata.name || file.name.replace('.leedoc', '');
    } else {
      projectName = file.name.replace('.leedoc', '').replace('.zip', '');
    }

    if (!options.existingProjectId) {
      const projects = await this.listProjects();
      const existingProject = projects.find(p => p.name === projectName);
      if (existingProject) {
        return {
          success: false,
          projectName,
          existingProject,
          needsConfirmation: true
        };
      }
    }

    let projectId: string;
    if (options.existingProjectId) {
      await this.deleteProject(options.existingProjectId);
      projectId = await this.createProject(projectName);
    } else {
      projectId = await this.createProject(projectName);
    }

    const importPromises: Promise<void>[] = [];

    zip.forEach((relativePath, zipEntry) => {
      if (relativePath.endsWith('project.json')) return;
      if (zipEntry.dir) return;
      if (relativePath.includes('_generated/')) return;

      const filename = relativePath.split('/').pop() ?? '';
      if (filename.startsWith('._') || filename === '.DS_Store' || filename.startsWith('__MACOSX')) return;
      if (relativePath.includes('__MACOSX/')) return;

      let cleanPath = relativePath;
      if (basePath && cleanPath.startsWith(basePath)) {
        cleanPath = cleanPath.slice(basePath.length);
      }

      if (cleanPath.startsWith('_components/')) {
        const name = cleanPath.replace('_components/', '');
        if (name) {
          importPromises.push(
            zipEntry.async('string').then(content =>
              this.db!.put('components', {
                projectId,
                name,
                content,
                lastModified: Date.now()
              }).then(() => {})
            )
          );
        }
      } else if (cleanPath.endsWith('.src.md')) {
        importPromises.push(
          zipEntry.async('string').then(content =>
            this.db!.put('files', {
              projectId,
              path: cleanPath,
              content,
              lastModified: Date.now()
            }).then(() => {})
          )
        );
      }
    });

    await Promise.all(importPromises);

    // Import file-level history snapshots
    const historyMetaFiles = Object.keys(zip.files).filter(f => {
      let cleanPath = f;
      if (basePath && cleanPath.startsWith(basePath)) {
        cleanPath = cleanPath.slice(basePath.length);
      }
      return cleanPath.startsWith('_history/') && cleanPath.endsWith('.json');
    });

    for (const metaPath of historyMetaFiles) {
      try {
        let cleanMetaPath = metaPath;
        if (basePath && cleanMetaPath.startsWith(basePath)) {
          cleanMetaPath = cleanMetaPath.slice(basePath.length);
        }

        const metaFile = zip.file(metaPath);
        if (!metaFile) continue;

        const metaContent = await metaFile.async('string');
        const metadata = JSON.parse(metaContent);

        const pathWithoutHistory = cleanMetaPath.replace('_history/', '').replace('.json', '');
        const lastSlashIndex = pathWithoutHistory.lastIndexOf('/');

        if (lastSlashIndex === -1) {
          console.warn(`Skipping old-format history snapshot: ${metaPath}`);
          continue;
        }

        const filePath = pathWithoutHistory.substring(0, lastSlashIndex);
        const snapshotId = pathWithoutHistory.substring(lastSlashIndex + 1);

        const contentPath = basePath
          ? `${basePath}_history/${filePath}/${snapshotId}.md`
          : `_history/${filePath}/${snapshotId}.md`;
        const contentEntry = zip.file(contentPath);

        if (!contentEntry) {
          console.warn(`Missing content file for snapshot: ${contentPath}`);
          continue;
        }

        const content = await contentEntry.async('string');

        await this.db.put('fileHistory', {
          projectId,
          filePath,
          id: snapshotId,
          timestamp: metadata.timestamp,
          reason: metadata.reason,
          content
        });
      } catch (err) {
        console.warn(`Failed to import history snapshot ${metaPath}:`, err);
      }
    }

    // Import media files
    const manifestPath = basePath ? `${basePath}_media/manifest.json` : '_media/manifest.json';
    const manifestFile = zip.file(manifestPath);
    if (manifestFile) {
      try {
        const manifestContent = await manifestFile.async('string');
        const manifest = JSON.parse(manifestContent) as Array<{
          filename: string;
          originalName: string;
          mimeType: string;
          uploadedBy?: string;
        }>;

        for (const item of manifest) {
          const mediaPath = basePath ? `${basePath}_media/${item.filename}` : `_media/${item.filename}`;
          const mediaEntry = zip.file(mediaPath);
          if (mediaEntry) {
            try {
              const arrayBuffer = await mediaEntry.async('arraybuffer');
              const file = new File([arrayBuffer], item.originalName, { type: item.mimeType });
              await this.uploadMedia(file, item.uploadedBy);
            } catch (err) {
              console.warn(`Failed to import media ${item.filename}:`, err);
            }
          }
        }
      } catch (err) {
        console.warn('Failed to import media manifest:', err);
      }
    }

    return { success: true, projectId, projectName };
  }

  // ==================== Helpers ====================

  private async updateProjectTimestamp(): Promise<void> {
    if (!this.currentProjectId || !this.db) return;

    const project = await this.db.get('projects', this.currentProjectId);
    if (project) {
      project.lastModified = Date.now();
      await this.db.put('projects', project);
    }
  }

  async isAvailable(): Promise<boolean> {
    try {
      if (typeof indexedDB === 'undefined') {
        return false;
      }
      await this.init();
      return true;
    } catch {
      return false;
    }
  }

  async getMetadata(): Promise<StorageMetadata> {
    const project = await this.getCurrentProject();
    return {
      name: this.name,
      description: 'Browser storage (IndexedDB)',
      projectId: this.currentProjectId,
      projectName: project?.name ?? null
    };
  }

  async clearAllData(): Promise<void> {
    await this.init();
    if (!this.db) return;

    const projects = await this.db.getAll('projects');
    for (const p of projects) {
      await this.deleteProject(p.id);
    }

    this.currentProjectId = null;
  }

  // ==================== File-Level Version History ====================

  private async getFileContent(filePath: string): Promise<string | null> {
    await this.init();

    if (!this.currentProjectId || !this.db) return null;

    if (filePath.startsWith('_components/')) {
      const name = filePath.replace('_components/', '');
      const component = await this.db.get('components', [this.currentProjectId, name]);
      return component?.content ?? null;
    } else {
      const file = await this.db.get('files', [this.currentProjectId, filePath]);
      return file?.content ?? null;
    }
  }

  async createSnapshot(filePath: string, reason: SnapshotReason): Promise<CreateSnapshotResult> {
    await this.init();

    if (!this.currentProjectId || !this.db) {
      return { success: false, error: 'No project selected' };
    }

    const content = await this.getFileContent(filePath);
    if (content === null) {
      return { success: false, error: 'File not found' };
    }

    const timestamp = Date.now();
    const snapshotId = generateSnapshotId(timestamp);

    await this.db.put('fileHistory', {
      projectId: this.currentProjectId,
      filePath,
      id: snapshotId,
      timestamp,
      reason,
      content
    });

    await this.pruneSnapshots(filePath);

    return { success: true, snapshotId };
  }

  async listSnapshots(filePath: string): Promise<SnapshotMetadata[]> {
    await this.init();

    if (!this.currentProjectId || !filePath || !this.db) {
      return [];
    }

    const allSnapshots = await this.db.getAllFromIndex('fileHistory', 'projectFile', [this.currentProjectId, filePath]);
    return allSnapshots
      .map(s => ({
        id: s.id,
        timestamp: s.timestamp,
        reason: s.reason
      }))
      .sort((a, b) => b.timestamp - a.timestamp);
  }

  async getSnapshot(filePath: string, snapshotId: string): Promise<Snapshot | null> {
    await this.init();

    if (!this.currentProjectId || !this.db) {
      return null;
    }

    const record = await this.db.get('fileHistory', [this.currentProjectId, filePath, snapshotId]);
    if (!record) return null;

    return {
      id: record.id,
      timestamp: record.timestamp,
      reason: record.reason,
      content: record.content
    };
  }

  async deleteSnapshot(filePath: string, snapshotId: string): Promise<DeleteResult> {
    await this.init();

    if (!this.currentProjectId || !this.db) {
      return { success: false };
    }

    await this.db.delete('fileHistory', [this.currentProjectId, filePath, snapshotId]);
    return { success: true };
  }

  private async getLastSnapshot(filePath: string): Promise<SnapshotMetadata | null> {
    const snapshots = await this.listSnapshots(filePath);
    return snapshots[0] ?? null;
  }

  async maybeCreateSnapshot(filePath: string): Promise<void> {
    if (!this.currentProjectId || !filePath) return;

    const lastSnapshot = await this.getLastSnapshot(filePath);
    const reason = shouldCreateSnapshot(lastSnapshot);

    if (reason) {
      await this.createSnapshot(filePath, reason);
    }
  }

  private async pruneSnapshots(filePath: string): Promise<void> {
    const snapshots = await this.listSnapshots(filePath);
    if (snapshots.length <= 5) return;

    const toKeep = applyRetentionPolicy(snapshots);
    const toKeepIds = new Set(toKeep.map(s => s.id));

    for (const snap of snapshots) {
      if (!toKeepIds.has(snap.id)) {
        await this.deleteSnapshot(filePath, snap.id);
      }
    }
  }

  async restoreSnapshot(filePath: string, snapshotId: string): Promise<RestoreSnapshotResult> {
    await this.init();

    if (!this.currentProjectId || !this.db) {
      return { success: false, error: 'No project selected' };
    }

    const snapshot = await this.getSnapshot(filePath, snapshotId);
    if (!snapshot) {
      return { success: false, error: 'Snapshot not found' };
    }

    if (filePath.startsWith('_components/')) {
      const name = filePath.replace('_components/', '');
      await this.db.put('components', {
        projectId: this.currentProjectId,
        name,
        content: snapshot.content,
        lastModified: Date.now()
      });
    } else {
      await this.db.put('files', {
        projectId: this.currentProjectId,
        path: filePath,
        content: snapshot.content,
        lastModified: Date.now()
      });
    }

    await this.updateProjectTimestamp();
    return { success: true, content: snapshot.content };
  }

  // ==================== Media Management ====================

  private async computeMediaHash(data: ArrayBuffer): Promise<string> {
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  }

  // Uses shared sanitizeMediaFilename from ../../shared/media-utils.ts
  // Uses shared getImageDimensions from ../../shared/media-utils.ts

  async uploadMedia(file: File, uploadedBy?: string): Promise<MediaUploadResult> {
    await this.init();

    if (!this.currentProjectId || !this.db) {
      return { success: false, error: 'No project selected' };
    }

    try {
      const data = await file.arrayBuffer();
      const contentHash = await this.computeMediaHash(data);

      // Check for duplicate by hash
      const existingMedia = await this.db.getAllFromIndex('media', 'contentHash', [this.currentProjectId, contentHash]);
      if (existingMedia.length > 0) {
        const existing = existingMedia[0];
        return {
          success: true,
          deduplicated: true,
          filename: existing.filename,
          url: `media://${existing.filename}`,
          markdown: `![${existing.originalName}](media://${existing.filename})`,
          size: existing.size,
          width: existing.width,
          height: existing.height,
        };
      }

      // Generate unique filename using shared utilities
      const sanitized = sanitizeMediaFilename(file.name);
      const projectId = this.currentProjectId;
      const db = this.db;
      const filename = await (async () => {
        let candidate = sanitized;
        let counter = 1;
        while (await db.get('media', [projectId, candidate])) {
          const ext = sanitized.includes('.') ? sanitized.substring(sanitized.lastIndexOf('.')) : '';
          const base = sanitized.includes('.') ? sanitized.substring(0, sanitized.lastIndexOf('.')) : sanitized;
          candidate = `${base}-${counter}${ext}`;
          counter++;
        }
        return candidate;
      })();

      // Get dimensions for images using shared utility
      const { width, height } = getImageDimensions(new Uint8Array(data), file.type);

      // Store in IndexedDB
      await this.db.put('media', {
        projectId: this.currentProjectId,
        filename,
        originalName: file.name,
        mimeType: file.type,
        size: file.size,
        width,
        height,
        data,
        uploadedBy,
        contentHash,
        createdAt: Date.now(),
      });

      await this.updateProjectTimestamp();

      return {
        success: true,
        filename,
        url: `media://${filename}`,
        markdown: `![${file.name}](media://${filename})`,
        size: file.size,
        width,
        height,
      };
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'Upload failed' };
    }
  }

  async listMedia(): Promise<MediaListResult> {
    await this.init();

    if (!this.currentProjectId || !this.db) {
      return { media: [], count: 0, totalSize: 0 };
    }

    const mediaRecords = await this.db.getAllFromIndex('media', 'projectId', this.currentProjectId);

    let totalSize = 0;
    const media: MediaItem[] = mediaRecords.map(m => {
      totalSize += m.size;
      return {
        filename: m.filename,
        originalName: m.originalName,
        mimeType: m.mimeType,
        size: m.size,
        width: m.width,
        height: m.height,
        uploadedBy: m.uploadedBy,
        createdAt: new Date(m.createdAt).toISOString(),
        url: `media://${m.filename}`,
        thumbnailUrl: this.getMediaUrl(m.filename),
      };
    });

    return {
      media: media.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
      count: media.length,
      totalSize,
    };
  }

  async getMediaUsage(filename: string): Promise<MediaUsage> {
    await this.init();

    if (!this.currentProjectId || !this.db) {
      return { filename, usedIn: [], isOrphaned: true };
    }

    const files = await this.db.getAllFromIndex('files', 'projectId', this.currentProjectId);
    const components = await this.db.getAllFromIndex('components', 'projectId', this.currentProjectId);

    const usedIn: string[] = [];
    const pattern = new RegExp(`media://${filename.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'g');

    for (const file of files) {
      if (pattern.test(file.content)) {
        usedIn.push(file.path);
      }
    }

    for (const component of components) {
      if (pattern.test(component.content)) {
        usedIn.push(`_components/${component.name}`);
      }
    }

    return {
      filename,
      usedIn,
      isOrphaned: usedIn.length === 0,
    };
  }

  async deleteMedia(filename: string): Promise<MediaDeleteResult> {
    await this.init();

    if (!this.currentProjectId || !this.db) {
      return { success: false };
    }

    await this.db.delete('media', [this.currentProjectId, filename]);
    await this.updateProjectTimestamp();

    return { success: true, deleted: 1 };
  }

  async bulkDeleteMedia(filenames: string[]): Promise<MediaDeleteResult> {
    await this.init();

    if (!this.currentProjectId || !this.db || filenames.length === 0) {
      return { success: false, deleted: 0 };
    }

    let deleted = 0;
    for (const filename of filenames) {
      await this.db.delete('media', [this.currentProjectId, filename]);
      deleted++;
    }

    await this.updateProjectTimestamp();

    return { success: true, deleted };
  }

  getMediaUrl(filename: string): string {
    // For browser storage, we create a blob URL on demand
    // This is a placeholder - actual blob URL will be created when rendering
    return `media://${filename}`;
  }

  /**
   * Get media data for rendering (creates a blob URL)
   */
  async getMediaBlobUrl(filename: string): Promise<string | null> {
    await this.init();

    if (!this.currentProjectId || !this.db) {
      return null;
    }

    const media = await this.db.get('media', [this.currentProjectId, filename]);
    if (!media) {
      return null;
    }

    const blob = new Blob([media.data], { type: media.mimeType });
    return URL.createObjectURL(blob);
  }

  // ==================== Checklist Mode ====================

  async getChecklist(filePath: string): Promise<string[] | null> {
    await this.init();

    if (!this.currentProjectId || !this.db) {
      return null;
    }

    const record = await this.db.get('checklist', [this.currentProjectId, filePath]);
    if (!record) return null;
    return record.checkedSlugs;
  }

  async saveChecklist(filePath: string, checkedSlugs: string[]): Promise<void> {
    await this.init();

    if (!this.currentProjectId || !this.db) {
      throw new Error('No project selected');
    }

    await this.db.put('checklist', {
      projectId: this.currentProjectId,
      filePath,
      checkedSlugs,
    });
  }

  async deleteChecklist(filePath: string): Promise<void> {
    await this.init();

    if (!this.currentProjectId || !this.db) {
      throw new Error('No project selected');
    }

    await this.db.delete('checklist', [this.currentProjectId, filePath]);
  }
}
